import ActiveProduct from '/components/products/pack-builder/one-flow/product-step/ActiveProduct';
import AddToCart from '/components/products/pack-builder/one-flow/product-step/AddToCart';
import AvailableProducts from '/components/products/pack-builder/one-flow/product-step/AvailableProducts';
import Box from '/components/products/pack-builder/one-flow/product-step/Box';
import ExtraItems from '/components/products/pack-builder/one-flow/product-step/ExtraItems';
import Loading from '/components/Loading';
import PackPrice from '/components/products/pack-builder/one-flow/product-step/PackPrice';
import PrintTypes from '/components/products/pack-builder/one-flow/product-step/PrintTypes';
import ProductConfigs from '/components/products/pack-builder/one-flow/product-step/ProductConfigs';
import ProductSelectorHeading from '/components/products/pack-builder/one-flow/product-step/ProductSelectorHeading';
import PropTypes from 'prop-types';
import Slots from '/components/products/pack-builder/one-flow/product-step/Slots';
import Typography from '/components/Typography';
import { getEmptySlots } from '/services/static/packs/pack-price';
import { getProduct } from '/services/products';
import { getSimilarPrints } from '/services/searchspring/';

import { useEffect, useState } from 'react';

const ProductStep = ({
	builderConfig,
	packConfig,
	parentProduct,
	setActiveProductType,
	updatePackConfig,
	packSizeOptions,
}) => {
	const [activeGroupIndex, setActiveGroupIndex] = useState(0);
	const [activeProduct, setActiveProduct] = useState({});
	const [availableProducts, setAvailableProducts] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [emptySlots, setEmptySlots] = useState([]);
	const [extraItems, setExtraItems] = useState([]);
	const [productFilters, setProductFilters] = useState({});
	const [selectedProductConfig, setSelectedProductConfig] = useState({});
	const [showAddedMessaging, setShowAddedMessaging] = useState(false);

	useEffect(() => {
		if (!builderConfig) {
			return;
		}

		const gorgiasChat = document.getElementById('gorgias-chat-container');

		if (gorgiasChat) {
			gorgiasChat.remove();
		}

		const defaultSlots = getEmptySlots(
			builderConfig.builderType,
			packConfig.selectedProducts.length,
			builderConfig.productCountMax,
		);
		setEmptySlots(defaultSlots);

		const defaultProductConfig = packConfig?.productConfigs?.[0];

		let defaultProductType;
		let defaultPrintTypeKey;
		let defaultSize;
		let updateFilters = false;

		if (!selectedProductConfig?.productType?.id && defaultProductConfig) {
			setSelectedProductConfig(defaultProductConfig);
		}

		// default to the first product config if it is available
		if (!productFilters.productType && defaultProductConfig) {
			defaultProductType = defaultProductConfig.productType.productType;
			updateFilters = true;
		}

		if (!productFilters.size && defaultProductConfig) {
			defaultSize = defaultProductConfig.size;
			updateFilters = true;
		}

		if (!productFilters.printType && defaultProductConfig.productType?.printTypes?.[0]?.key) {
			const defaultPrintType = defaultProductConfig.productType.printTypes.find((printType) => {
				return printType.isDefault;
			});

			defaultPrintTypeKey =
				defaultPrintType?.key || defaultProductConfig.productType.printTypes[0].key;

			updateFilters = true;
		}

		if (updateFilters) {
			setProductFilters({
				productType: defaultProductType,
				printType: defaultPrintTypeKey,
				size: defaultSize,
			});
		}
	}, [builderConfig, packConfig]);

	// get new prints when the flavor changes
	useEffect(() => {
		if (productFilters?.productType && productFilters.size) {
			void getPrints();
		}
	}, [productFilters]);

	const addToPack = async (product) => {
		const toUpdate = [...(packConfig?.selectedProducts || [])];

		const fullProduct = await getProduct(product.handle);

		const variant = fullProduct.variants.find((variant) => {
			return variant.title === selectedProductConfig.size;
		});

		const toPush = {
			image:
				product?.auxiliaryImages?.renderFrontTransparentSrc ||
				product?.auxiliaryImages?.renderFrontSrc ||
				fullProduct?.images?.[0].src,
			prices: fullProduct.prices,
			shopifyProductId: fullProduct.id,
			shopifyVariantId: variant.id,
			title: fullProduct.title,
		};

		toUpdate.push(toPush);
		setShowAddedMessaging(true);

		setTimeout(() => {
			setShowAddedMessaging(false);
		}, 1000);

		updatePackConfig('selectedProducts', toUpdate);
	};

	const getPrints = async () => {
		if (!productFilters.productType || !productFilters.size) {
			return;
		}

		try {
			const result = await getSimilarPrints(
				{
					productType: productFilters.productType,
				},
				productFilters.printType ? `print: ${productFilters.printType}` : null,
				productFilters.size,
			);

			if (result && result.length > 0) {
				setAvailableProducts(result);
				setActiveProduct(result[0]);
				setIsLoading(false);
			}
		} catch (error) {
			console.error('Error fetching prints:', error);
			setIsLoading(false);
		}
	};

	// remove an item from the pack by index
	const removeFromPack = (index) => {
		const toUpdate = [...(packConfig?.selectedProducts || [])];
		toUpdate.splice(index, 1);

		updatePackConfig('selectedProducts', toUpdate);
	};

	const updateSelectedProductConfig = (index) => {
		const productConfig = packConfig.productConfigs[index];
		setActiveGroupIndex(index);
		setSelectedProductConfig(productConfig);
		const toUpdate = { ...productFilters };
		toUpdate.productType = productConfig.productType.productType;
		toUpdate.size = productConfig.size;

		if (toUpdate.printType) {
			const matchingPrintType = productConfig.productType?.printTypes.find((printType) => {
				return toUpdate.printType === printType.key;
			});

			if (!matchingPrintType && productConfig?.productType?.printTypes?.[0]?.key) {
				toUpdate.printType = productConfig.productType.printTypes[0].key;
			}
		}

		setProductFilters(toUpdate);
	};

	const updateProductFilters = (key, value) => {
		const toUpdate = { ...productFilters };
		toUpdate[key] = value;
		setProductFilters(toUpdate);
	};

	if (isLoading) {
		return (
			<div className="min-h-[70vh] flex items-center justify-center">
				<Loading height="40px" width="40px" className="ml-2" />
			</div>
		);
	}

	return (
		<div className="grid md:grid-cols-2 max-w-7xl m-auto pt-12 min-h-[70vh] gap-2">
			<div className="flex items-center flex-col gap-4 text-center">
				<ActiveProduct
					activeGroupIndex={activeGroupIndex}
					addToPack={addToPack}
					builderConfig={builderConfig}
					packConfig={packConfig}
					product={activeProduct}
					showAddedMessaging={showAddedMessaging}
				/>
				<div className="hidden md:block">
					<Slots
						activeGroupIndex={activeGroupIndex}
						emptySlots={emptySlots}
						packConfig={packConfig}
						packSizeOptions={packSizeOptions}
						removeFromPack={removeFromPack}
					/>
				</div>
			</div>
			<div className="z-10 bg-v2-off-white flex flex-col gap-4">
				<ProductSelectorHeading builderConfig={builderConfig} packConfig={packConfig} />

				<ProductConfigs
					packConfig={packConfig}
					productFilters={productFilters}
					selectedProductConfig={selectedProductConfig}
					setActiveProductType={setActiveProductType}
					updateSelectedProductConfig={updateSelectedProductConfig}
				/>

				<PrintTypes
					productFilters={productFilters}
					selectedProductConfig={selectedProductConfig}
					updateProductFilters={updateProductFilters}
				/>

				<AvailableProducts
					activeProduct={activeProduct}
					availableProducts={availableProducts}
					setActiveProduct={setActiveProduct}
				/>

				<div className="flex gap-4 items-start justify-center">
					<ExtraItems
						extraItems={extraItems}
						extraItemsConfig={builderConfig?.extraItems}
						setExtraItems={setExtraItems}
						packConfig={packConfig}
						updatePackConfig={updatePackConfig}
					/>

					<Box
						builderConfig={builderConfig}
						packConfig={packConfig}
						updatePackConfig={updatePackConfig}
					/>
				</div>

				<div className="hidden md:block">
					<PackPrice builderConfig={builderConfig} packConfig={packConfig} />
				</div>
				<div className="sticky bottom-0 w-full bg-v2-off-white p-2">
					<div className="md:hidden">
						<div className="flex justify-between items-center">
							<PackPrice builderConfig={builderConfig} packConfig={packConfig} />
						</div>
						<Typography variant="heading-sm">Your Pack:</Typography>
						<Slots
							activeGroupIndex={activeGroupIndex}
							emptySlots={emptySlots}
							packConfig={packConfig}
							packSizeOptions={packSizeOptions}
							removeFromPack={removeFromPack}
						/>
					</div>
					<AddToCart
						builderConfig={builderConfig}
						packConfig={packConfig}
						parentProduct={parentProduct}
					/>
				</div>
			</div>
		</div>
	);
};

ProductStep.propTypes = {
	builderConfig: PropTypes.object,
	packConfig: PropTypes.object,
	packSizeOptions: PropTypes.array,
	parentProduct: PropTypes.object,
	setActiveProductType: PropTypes.func,
	updatePackConfig: PropTypes.func,
};

export default ProductStep;
